import React, { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Link, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Container, Stack, styled } from '@mui/system';
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import JsxParser from 'react-jsx-parser';
import { getInviteFormByPathGql, getInviteGql } from '../InviteResponse/gql';
import NotFound from '../../Components/NotFound';
import RsvpHeading from '../../Components/RsvpHeading';
import { GeneralContext } from '../../util/Store';
import GlobalStyles from '../../Components/GlobalStyles';
import { ThemeContext } from '../../util/ThemeStore';
import Error404Page from '../404';
import BookedOut from '../BookedOut';

const PaperBox = styled(Paper)({
  padding: '36px 36px 36px 36px',
  paddingBottom: 10,
  position: 'relative',
});

export default function Welcome() {
  const { inviteCode, path } = useParams();
  const snackbar = useSnackbar();
  const generalStore = useContext(GeneralContext);
  const themeStore = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigateKeepingParams = (targetPath) => {
    const query = searchParams.toString();
    navigate(query ? `${targetPath}?${query}` : targetPath);
  };

  const {
    data: inviteFormResult,
    loading: formLoading,
    error: inviteFormError,
  } = useQuery(getInviteFormByPathGql, {
    fetchPolicy: 'no-cache',
    variables: { path },
    onCompleted: ({ getInviteFormByPath }) => {
      generalStore.setFooterLinks(getInviteFormByPath.inviteForm?.frontendSettings?.footerLinks);
      if (getInviteFormByPath?.themeConfig?.typography) {
        themeStore.setTypography(getInviteFormByPath?.themeConfig?.typography);
      }
    },
  });

  const {
    data,
    loading,
  } = useQuery(getInviteGql, {
    fetchPolicy: 'no-cache',
    skip: !inviteCode,
    variables: {
      inviteCode,
    },
    onCompleted: ({ getInvite }) => {
      if (getInvite.status === 'accepted') {
        navigateKeepingParams(`/${path}/response/${inviteCode}/accepted`);
        return;
      }
      if (getInvite.status === 'declined') {
        navigateKeepingParams(`/${path}/response/${inviteCode}/declined`);
      }
    },
    onError: (error) => {
      if (error.networkError.statusCode === 404) {
        navigateKeepingParams(`/${path}/response/${inviteCode}/error`);
        return;
      }
      snackbar.showMessage('Fehler beim laden.');
    },
  });

  const inviteFormByPath = inviteFormResult?.getInviteFormByPath;
  const inviteForm = useMemo(() => inviteFormByPath?.inviteForm, [inviteFormByPath?.inviteForm]);
  const isBookedOut = inviteFormByPath?.isBookedOut === true;
  const inviteFormStatusCode = inviteFormError?.networkError?.statusCode || inviteFormError?.statusCode;
  useEffect(() => {
    if (!data?.getInvite) return;
    generalStore.setContactInfo(data?.getInvite?.inviteForm?.frontendSettings?.contactInfo);
  }, [data?.getInvite]);

  const renderContent = () => {
    if (loading) {
      return (
        <Stack alignItems="center" sx={{ mt: 5 }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (!inviteForm || (inviteForm.type !== 'registration' && !inviteCode)) return (<NotFound />);

    return (
      <>
        <JsxParser
          components={{ Typography }}
          jsx={inviteForm.description}
          autoCloseVoidElements
          onError={(err) => console.error(err)}
        />
        <Grid container direction="row" justifyContent="center" sx={{ pt: 4 }}>
          <Button
            variant="contained"
            to={!inviteCode ? `/${path}/response` : `/${path}/response/${inviteCode}`}
            component={Link}
          >
            {inviteForm?.frontendSettings?.welcomeButtonLabel || data?.getInvite?.inviteForm?.frontendSettings?.welcomeButtonLabel || 'Zur Registrierung'}
          </Button>
        </Grid>
      </>
    );
  };

  if (inviteFormStatusCode === 404) {
    return <Error404Page />;
  }

  if (formLoading) {
    return (
      <Container maxWidth="lg">
        <Stack alignItems="center" sx={{ mt: 5 }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (isBookedOut && !inviteCode) {
    return (
      <Container maxWidth="lg">
        <GlobalStyles
          customCss={inviteFormByPath?.customCss}
          colors={inviteFormByPath?.colors}
          font={inviteFormByPath?.font}
        />
        <RsvpHeading logoUrl={inviteFormByPath?.logoUrl} inviteForm={inviteForm} />
        <PaperBox sx={{ pb: 4, mb: 2 }} elevation={4}>
          <BookedOut />
        </PaperBox>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <GlobalStyles
        customCss={inviteFormResult?.getInviteFormByPath?.customCss}
        colors={inviteFormResult?.getInviteFormByPath?.colors}
        font={inviteFormResult?.getInviteFormByPath?.font}
      />
      <RsvpHeading logoUrl={inviteFormResult?.getInviteFormByPath?.logoUrl} inviteForm={inviteForm} />
      <PaperBox sx={{ pb: 4, mb: 2 }} elevation={4}>
        {renderContent()}
      </PaperBox>
    </Container>
  );
}
